/**
 * ForgotPasswordView - Password Reset Request Page
 * 
 * Allows users to request a password reset link via email.
 */

import { useEffect, useState } from 'react';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ForgotPasswordViewProps {
  onBackToLogin: () => void;
}

export const ForgotPasswordView: React.FC<ForgotPasswordViewProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Passwort zurücksetzen — estage.ai';
    return () => { document.title = prev; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });

      if (error) {
        setError(error.message);
      } else {
        setIsSuccess(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Background Pattern */}
       <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
      
      <div className="relative w-full max-w-md z-10">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/logo.png" alt="estage.ai" className="w-12 h-12" />
          <h1 className="text-3xl font-bold text-foreground tracking-tight">
            estage<span className="text-primary">.ai</span>
          </h1>
        </div>

        {/* Form Card */}
        <Card className="border-border/50 shadow-2xl backdrop-blur-sm bg-card/95">
          <CardContent className="p-8">
            {isSuccess ? (
              // Success State
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  E-Mail gesendet!
                </h2>
                <p className="text-muted-foreground text-sm mb-6">
                  Wir haben dir einen Link zum Zurücksetzen deines Passworts an{' '}
                  <span className="font-medium text-foreground">{email}</span> gesendet.
                  Bitte überprüfe auch deinen Spam-Ordner.
                </p>
                <Button
                  variant="outline"
                  onClick={onBackToLogin}
                  className="w-full gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Zurück zum Login
                </Button>
              </div>
            ) : (
              // Form State
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold mb-2">
                    Passwort vergessen?
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email Input */}
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        placeholder="deine@email.de"
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <p className="text-sm text-destructive">{error}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full gap-2"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Link senden
                      </>
                    )}
                  </Button>
                </form>

                {/* Back to Login */}
                <div className="mt-6 text-center">
                  <Button
                    variant="link"
                    onClick={onBackToLogin}
                    className="text-sm gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Zurück zum Login
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};
